import { Trash2 } from "lucide-react";
import type { MouseEvent, ReactNode } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export type DeleteConfirmationAlertDialogProps = {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  icon?: ReactNode;
  size?: "default" | "sm";
  mediaClassName?: string;
  contentClassName?: string;
  onContentClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** Test id of the confirm button — the labels are translated, so tests target this. */
  confirmTestId?: string;
  /** Test id of the cancel button, for the same reason. */
  cancelTestId?: string;
  /** The confirm button's look: destructive (default) or a plain primary action. */
  confirmVariant?: "destructive" | "default";
  /** Where focus goes on close, when the opener is gone (call preventDefault). */
  onCloseAutoFocus?: (event: Event) => void;
};

export function ConfirmAlertDialog({
  trigger,
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  isPending = false,
  icon,
  size = "sm",
  mediaClassName = "bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive",
  contentClassName,
  onContentClick,
  confirmTestId,
  cancelTestId,
  confirmVariant = "destructive",
  onCloseAutoFocus,
}: DeleteConfirmationAlertDialogProps) {
  const resolvedIcon = icon ?? <Trash2 />;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      {trigger ? <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger> : null}
      <AlertDialogContent
        size={size}
        className={contentClassName}
        onClick={onContentClick}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <AlertDialogHeader>
          <AlertDialogMedia className={mediaClassName}>{resolvedIcon}</AlertDialogMedia>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="cursor-pointer"
            variant="outline"
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isPending}
            className="cursor-pointer"
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
